import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { AuthState, User } from '../types';
import api from '../services/api';

// 创建初始认证状态
const initialAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
};

// 创建认证上下文
interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<{success: boolean, error?: string}>;
  logout: () => void;
  updateUser: (user: User) => void;
  refreshToken: () => Promise<{success: boolean, token?: string, error?: string}>;
  getCurrentUser: () => Promise<{success: boolean, user?: User, error?: string}>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 认证提供者组件
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(initialAuthState);
  const autoLoginAttempted = useRef(false);

  // 登出函数
  const logout = useCallback(async () => {
    try {
      if (authState.token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      // 忽略登出API错误
    } finally {
      // 清除所有认证相关的本地存储数据
      localStorage.removeItem('authState');
      localStorage.removeItem('userName');
      localStorage.removeItem('tokenExpiresAt');
      // 清除登录页面缓存数据
      localStorage.removeItem('lastUsername');
      localStorage.removeItem('savedPassword');
      localStorage.removeItem('autoLogin');
      // 重置自动登录标志，确保下次登录时能正常触发自动登录逻辑
      autoLoginAttempted.current = false;
      // 最后更新状态，确保路由能正确重定向
      setAuthState(initialAuthState);
    }
  }, [authState.token]);

  // 获取当前用户信息
  const getCurrentUser = useCallback(async (): Promise<{success: boolean, user?: User, error?: string}> => {
    try {
      if (!authState.token) {
        return { success: false, error: 'no_token' };
      }

      const response = await api.get('/auth/me');
      const { id, username, name, role, phoneNumber, teamId, teamName, routeTemplate, isUploadRequired } = response.data;

      // 只取展示与权限所需的小字段；team（含 pavilionData 长文本）不进 authState，
      // 否则每次刷新都会把整段展馆数据写进 localStorage
      const userInfo: User = {
        id: id,
        username: username,
        name: name,
        role: role,
        phoneNumber: phoneNumber || undefined,
        teamId: teamId || undefined,
        teamName: teamName || undefined,
        routeTemplate: routeTemplate || undefined,
        isUploadRequired: isUploadRequired || false,
      };

      setAuthState(prev => {
        // 后台资料未变化时保持原引用，避免定时刷新引起整棵组件树无谓重渲染
        if (prev.user && JSON.stringify(prev.user) === JSON.stringify(userInfo)) {
          return prev;
        }
        return {
          ...prev,
          user: userInfo,
        };
      });

      return { success: true, user: userInfo };
    } catch (error: any) {
      if (error.response?.status === 401) {
        logout();
        return { success: false, error: 'invalid_token' };
      }
      return { success: false, error: 'get_user_failed' };
    }
  }, [authState.token, logout]);

  // 初始化时从localStorage恢复认证状态
  useEffect(() => {
    if (autoLoginAttempted.current) {
      return;
    }

    const savedAuthState = localStorage.getItem('authState');
    if (savedAuthState) {
      try {
        const parsedState = JSON.parse(savedAuthState);
        console.log('Restoring auth state from localStorage:', parsedState);

        // 检查token和userName，两者都必须存在才能恢复登录状态
        const savedUserName = localStorage.getItem('userName');
        if (parsedState.token && savedUserName) {
          autoLoginAttempted.current = true;

          setAuthState({
            isAuthenticated: !!parsedState.token,
            user: parsedState.user || null,
            token: parsedState.token
          });

          // 自动登录后立即获取完整的用户信息，确保routeTemplate等字段正确加载
          getCurrentUser().then(result => {
            if (result.success && result.user) {
              console.log('Auto-login: Successfully refreshed user info with routeTemplate:', result.user.routeTemplate);
            } else {
              console.warn('Auto-login: Failed to refresh user info, using cached data:', result.error);
              // 如果获取失败，确保使用默认的routeTemplate
              if (parsedState.user && !parsedState.user.routeTemplate) {
                const updatedUser = {
                  ...parsedState.user,
                  routeTemplate: 'pro-routes.json'
                };
                setAuthState(prev => ({
                  ...prev,
                  user: updatedUser,
                }));
              }
            }
          });
        } else {
          // 如果token或userName缺失，清除所有认证数据并要求重新登录
          console.log('Missing token or userName, requiring login');
          console.log('Token exists:', !!parsedState.token);
          console.log('UserName exists:', !!savedUserName);
          console.log('Saved authState:', parsedState);

          // 尝试从authState中恢复userName
          if (parsedState.token && !savedUserName && parsedState.user) {
            const userNameFromState = parsedState.user.name || parsedState.user.username;
            if (userNameFromState) {
              console.log('Recovering userName from authState:', userNameFromState);
              localStorage.setItem('userName', userNameFromState);
              // 重新尝试自动登录
              autoLoginAttempted.current = true;
              setAuthState({
                isAuthenticated: true,
                user: parsedState.user,
                token: parsedState.token
              });
              return;
            }
          }

          // 如果无法恢复，清除所有认证数据
          localStorage.removeItem('authState');
          localStorage.removeItem('tokenExpiresAt');
          localStorage.removeItem('userName');
          // 重置自动登录标志，确保下次能正常触发自动登录
          autoLoginAttempted.current = false;
        }
      } catch (error) {
        console.error('Error parsing authState from localStorage:', error);
        localStorage.removeItem('authState');
        localStorage.removeItem('tokenExpiresAt');
        localStorage.removeItem('userName');
        // 重置自动登录标志，确保下次能正常触发自动登录
        autoLoginAttempted.current = false;
      }
    } else {
      // 如果没有保存的认证状态，确保重置自动登录标志
      autoLoginAttempted.current = false;
    }
  }, [getCurrentUser]);

  // 保存最新版 getCurrentUser 到 ref，供后台刷新定时器使用（避免定时器依赖导致反复重建）
  const getCurrentUserRef = useRef(getCurrentUser);
  useEffect(() => {
    getCurrentUserRef.current = getCurrentUser;
  }, [getCurrentUser]);

  // 已登录期间定期/回到页面时静默刷新用户资料：
  // 团队管理修改团队名称、路由模板等配置后，存量登录用户无需重新登录即可看到最新值
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.token) {
      return;
    }

    const refreshUserInfo = () => {
      // 静默刷新，失败时保留现有展示数据，等下个周期重试
      getCurrentUserRef.current().catch(() => undefined);
    };

    const intervalId = window.setInterval(refreshUserInfo, 5 * 60 * 1000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUserInfo();
      }
    };

    window.addEventListener('focus', refreshUserInfo);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshUserInfo);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authState.isAuthenticated, authState.token]);

  // 当认证状态变化时保存到本地存储
  useEffect(() => {
    if (authState.isAuthenticated && authState.token && authState.user) {
      localStorage.setItem('authState', JSON.stringify(authState));
      // 不再手动设置过期时间，使用服务器返回的过期时间
    } else {
      localStorage.removeItem('authState');
      localStorage.removeItem('tokenExpiresAt');
      localStorage.removeItem('userName');
    }
  }, [authState]);



// 登录函数 - 调用后端API
  const login = async (username: string, password: string): Promise<{ success: boolean, error?: string }> => {
    try {
      const response = await api.post('/auth/login', { username, password });

      if (response.status !== 200) {
        return { success: false, error: 'login_failed' };
      }

      const { token, user, expiresAt } = response.data;

      const userInfo = {
        id: user.id,
        username: user.username,
        name: user.name,
        role: user.role,
        teamId: user.teamId || undefined,
        teamName: user.teamName || undefined,
        routeTemplate: user.routeTemplate || undefined,
        isUploadRequired: user.isUploadRequired || false,
      };

      setAuthState({
        isAuthenticated: true,
        user: userInfo,
        token: token,
      });

      if (expiresAt) {
        localStorage.setItem('tokenExpiresAt', expiresAt);
      }

      // 总是保存userName，优先使用user.name，如果没有则使用username
      const userNameToSave = user.name || user.username || username;
      if (userNameToSave) {
        localStorage.setItem('userName', userNameToSave);
        console.log('Saved userName to localStorage:', userNameToSave);
      } else {
        console.warn('无法保存userName到localStorage，所有字段都为空:', {
          user,
          username
        });
      }

      return { success: true };
    } catch (error: any) {
      if (error.response?.status === 401) {
        return { success: false, error: 'invalid_credentials' };
      }
      return { success: false, error: 'login_failed' };
    }
  };

  // 更新用户信息
  const updateUser = (user: User) => {
    setAuthState(prev => ({
      ...prev,
      user,
    }));
  };

  // 刷新token
  const refreshToken = useCallback(async (): Promise<{success: boolean, token?: string, error?: string}> => {
    try {
      if (!authState.token) {
        return { success: false, error: 'no_token' };
      }

      const response = await api.post('/auth/refresh', { token: authState.token });
      const { token: newToken, expiresAt } = response.data;

      setAuthState(prev => ({
        ...prev,
        token: newToken,
      }));

      // 更新token过期时间
      if (expiresAt) {
        localStorage.setItem('tokenExpiresAt', expiresAt);
      }

      return { success: true, token: newToken };
    } catch (error: any) {
      console.error('Token refresh failed:', error);

      if (error.response?.status === 401) {
        // Token无效，需要重新登录
        logout();
        return { success: false, error: 'invalid_token' };
      }

      return { success: false, error: 'refresh_failed' };
    }
  }, [authState.token, logout]);

// 定期检查token过期并自动刷新
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.token) {
      return;
    }

    const checkTokenExpiration = async () => {
      const expiresAt = localStorage.getItem('tokenExpiresAt');
      if (!expiresAt) {
        return;
      }

      const now = new Date().getTime();
      const expirationTime = new Date(expiresAt).getTime();
      const timeUntilExpiration = expirationTime - now;

      // 如果token将在5分钟内过期，自动刷新
      if (timeUntilExpiration < 5 * 60 * 1000 && timeUntilExpiration > 0) {
        console.log('Token即将过期，自动刷新...');
        const result = await refreshToken();
        if (result.success) {
          console.log('Token刷新成功');
        } else {
          console.warn('Token刷新失败:', result.error);
        }
      }
    };

    const intervalId = setInterval(checkTokenExpiration, 5 * 60 * 1000);

    // 延迟执行初始检查，避免在组件初始化时立即触发
    const initialCheckTimeout = setTimeout(() => {
      checkTokenExpiration();
    }, 1000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(initialCheckTimeout);
    };
  }, [authState.isAuthenticated, authState.token, refreshToken]);

  // 提供认证上下文值
  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    updateUser,
    refreshToken,
    getCurrentUser,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// 自定义钩子，用于访问认证上下文
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};